import { Field, FieldProps } from 'formik';
import React, { ReactNode } from 'react';
import { InjectedIntl, injectIntl } from 'react-intl';
import styled from 'styled-components';
import {
    IAddress,
    IAddressFieldConfig,
} from '../../../details/addresses/address/address.interface';
import { AddressFormatter } from '../../../details/addresses/address/addressFormatter.service';
import { AddressFormFields } from '../../../details/addresses/addressForm/AddressFormFields';
import SearchSelect from '../components/SearchSelect';
import { COLORS } from '../constants/styles';
import { TranslationHelpers } from '../../../i18n/translationHelpers';
import { IErrorMessage } from '../components/errorMessage/errorMessage.type';
import { SupportedLanguage } from '../../../config/constants/supportedLanguage.enum';

interface IAddressSelectorItem extends IAddress {
    searchValue: string;
}

interface IAddressSelectorProps {
    addressFieldsValues: Partial<IAddress>;
    addresses?: IAddress[];
    value?: string;
    selectedAddress?: IAddress;
    intl: InjectedIntl;
    addressFieldConfig: IAddressFieldConfig[][];
    locale: SupportedLanguage;
    addressTemplate?: string;
}

interface IAddressSelectorState {
    addingAddress?: boolean;
    editingAddress?: boolean;
    selectedAddressItem?: IAddressSelectorItem;
}

const AddAddressButton = styled.button`
    background: none;
    border: none;
    border-radius: 5px;
    color: ${COLORS.rakutenRed};
    cursor: pointer;
    padding: 10px;
    text-align: left;
    width: 100%;
`;

const SelectedAddressWrapper = styled.div``;

const SelectedAddressLabel = styled.span`
    color: ${COLORS.grey7};
    font-size: 0.75rem;
`;

const SelectedAddress = styled.div`
    background: ${COLORS.grey2};
    border-radius: 3px;
    font-size: 0.875rem;
    margin: 10px 0;
    padding: 15px 20px;
`;

const SelectedAddressControls = styled.div`
    display: flex;
    justify-content: flex-end;

    button {
        border: none;
        border-right: solid 1px ${COLORS.rakutenRed};
        background: none;
        color: ${COLORS.rakutenRed};
        cursor: pointer;
        margin-top: 10px;
        padding: 0 5px;

        &:last-child {
            border-right: none;
        }
    }
`;

class AddressSelector extends React.Component<IAddressSelectorProps, IAddressSelectorState> {
    private readonly ID: string;
    private readonly ERROR_REQUIRED_ID: string;

    constructor(props: IAddressSelectorProps) {
        super(props);

        this.ID = 'addressUuid';
        this.ERROR_REQUIRED_ID = 'error.billing_address_required';

        const { selectedAddress } = this.props;

        this.state = {
            selectedAddressItem: selectedAddress
                ? {
                      ...selectedAddress,
                      searchValue: AddressFormatter.formatToLine(selectedAddress),
                  }
                : undefined,
        };

        this.addAddress = this.addAddress.bind(this);
        this.changeAddress = this.changeAddress.bind(this);
        this.renderAddressSelect = this.renderAddressSelect.bind(this);
        this.selectAddress = this.selectAddress.bind(this);
        this.validate = this.validate.bind(this);
    }

    public render() {
        return <Field name={this.ID} validate={this.validate} render={this.renderAddressSelect} />;
    }

    private validate(value: string): IErrorMessage {
        const { intl } = this.props;
        const { addingAddress } = this.state;

        if (!value && !addingAddress) {
            return TranslationHelpers.getMessageDescriptor(this.ERROR_REQUIRED_ID);
        }

        return '';
    }

    private renderAddressSelect({
        field,
        form: { touched, errors, setFieldValue, setFieldTouched },
    }: FieldProps): ReactNode {
        const {
            addresses,
            addressFieldsValues,
            intl,
            addressFieldConfig,
            addressTemplate,
            locale,
        } = this.props;
        const { addingAddress, editingAddress, selectedAddressItem } = this.state;
        const isTouched = touched && touched[this.ID];
        const errorMessage = isTouched && errors && errors[this.ID] ? errors[this.ID] : undefined;
        const items: IAddressSelectorItem[] = addresses
            ? addresses.map((address) => ({
                  ...address,
                  searchValue: AddressFormatter.formatToLine(address, addressTemplate),
              }))
            : [];
        const addNewAddressText = intl.formatMessage(
            TranslationHelpers.getMessageDescriptor('address.new_billing'),
        );
        const onAddClick = (() => {
            setFieldValue('addressUuid', undefined);
            this.addAddress();
        }).bind(this);
        const onSelectAddressClick = ((item: IAddressSelectorItem) => {
            if (item) {
                setFieldValue('addressUuid', item.addressUuid);
                this.selectAddress(item);
            }
        }).bind(this);

        if (selectedAddressItem && !editingAddress) {
            return (
                <SelectedAddressWrapper>
                    <SelectedAddressLabel>
                        {TranslationHelpers.getFormattedMessage('address.billing.choose')}
                    </SelectedAddressLabel>
                    <SelectedAddress>
                        {AddressFormatter.formatToReactNode(
                            selectedAddressItem,
                            true,
                            false,
                            false,
                            locale,
                            addressTemplate,
                        )}
                    </SelectedAddress>
                    <SelectedAddressControls>
                        <button onClick={this.changeAddress}>
                            {TranslationHelpers.getFormattedMessage('button.change')}
                        </button>
                    </SelectedAddressControls>
                </SelectedAddressWrapper>
            );
        }

        const showAddressForm = !addresses || addingAddress;

        return (
            <div>
                <SearchSelect
                    autoComplete="off"
                    addText={addNewAddressText}
                    id={this.ID}
                    items={items}
                    placeholder={intl.formatMessage(
                        TranslationHelpers.getMessageDescriptor('label.address_selector'),
                    )}
                    selectedItem={selectedAddressItem}
                    searchKey="searchValue"
                    onSelect={onSelectAddressClick}
                    onAddClick={onAddClick}
                    required={true}
                    dummySelectedText={showAddressForm ? addNewAddressText : undefined}
                    qaId="address"
                    error={errorMessage}
                    setTouched={setFieldTouched}
                    showAddressForm={showAddressForm}
                />
                {showAddressForm ? (
                    <AddressFormFields
                        autoFocus={addingAddress}
                        values={addressFieldsValues}
                        addressFieldConfig={addressFieldConfig}
                    />
                ) : (
                    ''
                )}
            </div>
        );
    }

    private addAddress() {
        this.setState({
            addingAddress: true,
            editingAddress: true,
            selectedAddressItem: undefined,
        });
    }

    private changeAddress() {
        this.setState({
            addingAddress: false,
            editingAddress: true,
        });
    }

    private selectAddress(address: IAddressSelectorItem) {
        this.setState({ selectedAddressItem: address, editingAddress: false });
    }
}

export default injectIntl(AddressSelector);
